import { useEffect } from 'react';

interface UsePiSDKProps {
  piSDKLoaded: boolean;
  setPiSDKLoaded: (loaded: boolean) => void;
  setError: (error: string | null) => void;
  addDebugLog: (message: string) => void;
}

export const usePiSDK = ({
  piSDKLoaded,
  setPiSDKLoaded,
  setError,
  addDebugLog,
}: UsePiSDKProps) => {
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds total (50 * 100ms)

    const loadPiSDKManually = () => {
      addDebugLog('🔄 Attempting to load Pi SDK manually...');

      // Remove existing script if any
      const existingScript = document.querySelector('script[src*="pi-sdk.js"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Create new script element
      const script = document.createElement('script');
      script.src = 'https://sdk.minepi.com/pi-sdk.js';
      script.async = true;
      script.onload = () => {
        addDebugLog('✅ Pi SDK loaded manually');
        setTimeout(checkPiSDK, 100); // Give it a moment to initialize
      };
      script.onerror = () => {
        addDebugLog('❌ Failed to load Pi SDK manually');
        setError(
          'Unable to load Pi Network SDK. Please check your internet connection.'
        );
      };

      document.head.appendChild(script);
    };

    const checkPiSDK = () => {
      addDebugLog(
        `🔍 Checking Pi SDK... Attempt ${retryCount + 1}/${maxRetries}`
      );

      if (window.Pi) {
        addDebugLog('✅ Pi SDK found!');

        // Properly detect production environment
        const isProduction = window.location.hostname === 'koja-classics.com';
        addDebugLog(
          `🔧 Pi SDK Mode: ${isProduction ? 'PRODUCTION' : 'SANDBOX'}`
        );
        addDebugLog(`🌐 Current hostname: ${window.location.hostname}`);

        try {
          window.Pi.init({
            version: '2.0',
            sandbox: !isProduction, // Use production mode on koja-classics.com
          });
          addDebugLog('🚀 Pi SDK initialized successfully');
          setPiSDKLoaded(true);
        } catch (error) {
          addDebugLog(`❌ Pi SDK initialization failed: ${error}`);
          setError(
            'Pi Network SDK initialization failed. Please refresh the page.'
          );
        }
      } else {
        retryCount++;
        if (retryCount >= maxRetries) {
          addDebugLog('❌ Pi SDK failed to load after maximum retries');
          addDebugLog('🔄 Trying manual SDK loading...');
          loadPiSDKManually();
          return;
        }

        // Retry after 100ms if Pi SDK not loaded yet
        setTimeout(checkPiSDK, 100);
      }
    };

    // Check if the script tag exists
    const piScript = document.querySelector('script[src*="pi-sdk.js"]');
    addDebugLog(`📜 Pi SDK script tag found: ${!!piScript}`);

    if (piScript) {
      addDebugLog(`📜 Pi SDK script src: ${piScript.getAttribute('src')}`);
    }

    checkPiSDK();
  }, [setPiSDKLoaded, setError, addDebugLog]);

  const handleRetryPiSDK = () => {
    setError(null);
    setPiSDKLoaded(false);

    // Force reload the page to restart Pi SDK loading
    window.location.reload();
  };

  return {
    handleRetryPiSDK,
  };
};
